import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FoodsIcon, FoodsIllustrations, FoodsTags } from "@foodpilot/foods";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Navigate } from "react-router";
import { useAcceptConsentGate } from "src/api/client-api/consent/useAcceptConsentGate";
import { useGetConsentGateItems, type ConsentGateItem } from "src/api/client-api/consent/useGetConsentGateItems";
import { useGetConsentPreferences, type ConsentPreference } from "src/api/client-api/consent/useGetConsentPreferences";
import { useUpdateConsentPreferences } from "src/api/client-api/consent/useUpdateConsentPreferences";
import { HTMLBox } from "src/components/HTMLBox/HTMLBox";
import { z } from "zod";
import { URLHandler } from "src/services/URLHandler";
import { UserService } from "src/services/UserService";
import { CONSENT_GOVERNANCE_FEATURE_SWITCH } from "src/api/client-api/consent/constants";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { getConsentCategoryConfig, hasKnownConsentCategory } from "src/components/Consent/consentCategoryConfig";
import { getConsentPreviewText } from "src/components/Consent/ConsentDocumentPreviewDialog";
import { getConsentDisplayTitle } from "src/components/Consent/consentDisplay";
import { DocumentRow } from "./components/DocumentRow";

const consentGateRequiredDocumentsMessage =
    "Please accept the required documents below to access the platform. You can also configure your communication preferences.";

const breakLongTextSx = {
    hyphens: "auto",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
} as const;

const createConsentGateSchema = (requiredVersionIds: number[], errorMessage: string) =>
    z.object({
        versionIds: z.array(z.number()).superRefine((selectedVersionIds, context) => {
            const missingVersionId = requiredVersionIds.find((versionId) => !selectedVersionIds.includes(versionId));

            if (missingVersionId === undefined) {
                return;
            }

            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: errorMessage,
            });
        }),
    });

export type ConsentWrite = {
    versionId: number;
};

type ConsentGateFormValues = {
    versionIds: number[];
};

type OptionalConsentRowProps = {
    isChecked: boolean;
    onToggle: () => void;
    preference: ConsentPreference;
};

const OptionalConsentRow = ({ isChecked, onToggle, preference }: OptionalConsentRowProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const displayTitle = getConsentDisplayTitle(preference.title, preference.version);
    const previewText = getConsentPreviewText(preference.summary, preference.content);

    return (
        <Box
            sx={{
                bgcolor: isChecked ? theme.palette.primary[50] : "rgba(42, 43, 39, 0.025)",
                border: `1px solid ${isChecked ? theme.palette.primary.main : theme.custom.grey[500]}`,
                borderRadius: theme.spacing(1),
                p: theme.spacing(1.75, 2),
                transition: "background-color 0.15s, border-color 0.15s",
                userSelect: "none",
                width: "100%",
            }}
        >
            <Box
                sx={{
                    columnGap: theme.spacing(1.5),
                    display: "grid",
                    gridTemplateColumns: "auto minmax(0, 1fr)",
                    rowGap: theme.spacing(0.5),
                    width: "100%",
                }}
            >
                <Box sx={{ alignItems: "center", alignSelf: "stretch", display: "flex", gridColumn: 1, gridRow: 1 }}>
                    <Checkbox
                        checked={isChecked}
                        inputProps={{
                            "aria-label": displayTitle,
                        }}
                        onChange={onToggle}
                        size="small"
                        sx={{
                            color: theme.custom.grey[1500],
                            flexShrink: 0,
                            p: 0,
                            "&.Mui-checked": {
                                color: theme.palette.primary.main,
                            },
                        }}
                    />
                </Box>
                <Typography
                    variant="body"
                    sx={{
                        color: theme.custom.grey[2800],
                        fontWeight: 700,
                        gridColumn: 2,
                        gridRow: 1,
                        lineHeight: 1.35,
                        minWidth: 0,
                        ...breakLongTextSx,
                    }}
                >
                    {displayTitle}
                </Typography>
                <Stack
                    alignItems="center"
                    direction="row"
                    flexWrap="wrap"
                    gap={theme.spacing(1)}
                    sx={{ gridColumn: 2 }}
                >
                    <FoodsTags label={t("consent_gate.optional_tag", "Optional")} variant="tags-green" />
                </Stack>
                {previewText && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.custom.grey[2000],
                            gridColumn: 2,
                            lineHeight: 1.45,
                            ...breakLongTextSx,
                        }}
                    >
                        {previewText}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export const Consents = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);
    const [viewingDocument, setViewingDocument] = useState<ConsentGateItem | null>(null);
    const [isDocumentDialogOpen, setIsDocumentDialogOpen] = useState(false);
    const [selectedOptionalItemIds, setSelectedOptionalItemIds] = useState<Set<number>>(new Set());
    const { isFeatureActivated } = useApplicationParameters();
    const isConsentGovernanceEnabled = isFeatureActivated(CONSENT_GOVERNANCE_FEATURE_SWITCH);
    const {
        consentGateItems,
        isLoading: isConsentGateItemsLoading,
        isFetched: isConsentGateItemsFetched,
        isError: isConsentGateItemsError,
        error: consentGateItemsError,
    } = useGetConsentGateItems({ enabled: isConsentGovernanceEnabled });
    const {
        consentPreferences,
        isLoading: isConsentPreferencesLoading,
        isFetched: isConsentPreferencesFetched,
        isError: isConsentPreferencesError,
        error: consentPreferencesError,
    } = useGetConsentPreferences({ enabled: isConsentGovernanceEnabled });

    const pendingConsentItems = consentGateItems ?? [];
    const requiredVersionIds = useMemo(() => pendingConsentItems.map((item) => item.versionId), [pendingConsentItems]);
    const unansweredConsentPreferences = useMemo(
        () => (consentPreferences ?? []).filter((preference) => preference.updatedAt == null),
        [consentPreferences],
    );
    const hasRequiredDocuments = requiredVersionIds.length > 0;
    const hasPendingOptionalPreferences = unansweredConsentPreferences.length > 0;
    const isOptionalOnly = !hasRequiredDocuments && hasPendingOptionalPreferences;

    const methods = useForm<ConsentGateFormValues>({
        defaultValues: {
            versionIds: [],
        },
        resolver: zodResolver(
            createConsentGateSchema(
                requiredVersionIds,
                t("consent_gate.description", consentGateRequiredDocumentsMessage),
            ),
        ),
    });

    const { mutate: acceptConsentGate, isLoading: isSubmitting, isSuccess } = useAcceptConsentGate();
    const { mutate: updateConsentPreferences, isLoading: isSavingConsentPreferences } = useUpdateConsentPreferences();
    const [isCompleted, setIsCompleted] = useState(false);

    const { control, handleSubmit, setValue, watch } = methods;
    const selectedVersionIds = watch("versionIds");
    const selectedCount = selectedVersionIds.length;
    const totalCount = requiredVersionIds.length;
    const allChecked = hasRequiredDocuments && selectedCount === totalCount;
    const canValidate = !hasRequiredDocuments || allChecked;
    const viewingDocumentCategoryConfig = getConsentCategoryConfig(viewingDocument?.category);
    const viewingDocumentCategoryLabel = t(
        viewingDocumentCategoryConfig.labelKey,
        viewingDocumentCategoryConfig.labelFallback,
    );
    const showViewingDocumentCategory = hasKnownConsentCategory(viewingDocument?.category);
    const isBusy =
        isConsentGateItemsLoading || isConsentPreferencesLoading || isSubmitting || isSavingConsentPreferences;

    const displayError =
        error ??
        (isConsentGateItemsError ?
            consentGateItemsError instanceof Error ?
                consentGateItemsError.message
            :   t("une_erreur_s_est_produite")
        : isConsentPreferencesError ?
            consentPreferencesError instanceof Error ?
                consentPreferencesError.message
            :   t("une_erreur_s_est_produite")
        :   null);

    useEffect(() => {
        setSelectedOptionalItemIds(
            new Set(
                unansweredConsentPreferences
                    .filter((preference) => preference.enabled)
                    .map((preference) => preference.itemId),
            ),
        );
    }, [unansweredConsentPreferences]);

    if (!isConsentGovernanceEnabled) {
        return <Navigate to={URLHandler.home()} replace />;
    }

    if (
        isCompleted ||
        isSuccess ||
        UserService.isImpersonationActive() ||
        (isConsentGateItemsFetched &&
            isConsentPreferencesFetched &&
            pendingConsentItems.length === 0 &&
            !hasPendingOptionalPreferences)
    ) {
        return <Navigate to={URLHandler.home()} replace />;
    }

    const toggleVersionId = (versionId: number) => {
        const current = methods.getValues("versionIds");
        const next = current.includes(versionId) ? current.filter((id) => id !== versionId) : [...current, versionId];
        setValue("versionIds", next, { shouldValidate: true });
    };

    const openDocumentViewer = (item: ConsentGateItem) => {
        setViewingDocument(item);
        setIsDocumentDialogOpen(true);
    };

    const acceptAndCloseDocument = () => {
        if (viewingDocument) {
            const current = methods.getValues("versionIds");
            if (!current.includes(viewingDocument.versionId)) {
                setValue("versionIds", [...current, viewingDocument.versionId], { shouldValidate: true });
            }
        }
        setIsDocumentDialogOpen(false);
        setViewingDocument(null);
    };

    const toggleOptionalPreference = (itemId: number) => {
        setSelectedOptionalItemIds((current) => {
            const next = new Set(current);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }

            return next;
        });
    };

    const acceptRequiredDocuments = () => {
        if (requiredVersionIds.length === 0) {
            setError(null);
            setIsCompleted(true);
            return;
        }

        acceptConsentGate(
            { versionIds: requiredVersionIds },
            {
                onSuccess: () => {
                    setError(null);
                    setIsCompleted(true);
                },
                onError: (mutationError) => {
                    setError(mutationError instanceof Error ? mutationError.message : t("une_erreur_s_est_produite"));
                },
            },
        );
    };

    const submitConsentGate = () => {
        methods.clearErrors();
        setError(null);

        if (unansweredConsentPreferences.length === 0) {
            acceptRequiredDocuments();
            return;
        }

        updateConsentPreferences(
            {
                preferences: unansweredConsentPreferences.map((preference) => ({
                    itemId: preference.itemId,
                    enabled: selectedOptionalItemIds.has(preference.itemId),
                })),
            },
            {
                onSuccess: acceptRequiredDocuments,
                onError: (mutationError) => {
                    setError(mutationError instanceof Error ? mutationError.message : t("une_erreur_s_est_produite"));
                },
            },
        );
    };

    return (
        <FormProvider {...methods}>
            <Box
                sx={{
                    bgcolor: theme.palette.background.paper,
                    borderRadius: theme.spacing(2),
                    boxShadow: "0 4px 8px 0 rgba(42, 43, 39, 0.08)",
                    border: `1px solid ${theme.custom.grey[500]}`,
                    position: "relative",
                    width: "100%",
                    maxWidth: "520px",
                }}
            >
                {isBusy && (
                    <Stack
                        sx={{
                            background: "rgba(255, 255, 255, 0.5)",
                            position: "absolute",
                            inset: theme.spacing(2.5),
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                            borderRadius: theme.spacing(1),
                        }}
                    >
                        <CircularProgress />
                    </Stack>
                )}

                <Stack
                    component="form"
                    onSubmit={handleSubmit(submitConsentGate, (errors) => {
                        setError(errors.versionIds?.message ?? t("une_erreur_s_est_produite"));
                    })}
                    autoComplete="off"
                    sx={{
                        p: theme.spacing(4),
                        gap: theme.spacing(3),
                        alignItems: "center",
                    }}
                >
                    {/* Illustration */}
                    <FoodsIllustrations illustration="consents" />

                    {/* Title */}
                    <Typography variant="h2" align="center" sx={{ fontWeight: 700, width: "100%", ...breakLongTextSx }}>
                        {isOptionalOnly ?
                            t("consent_gate.optional_only_title", "Update my preferences")
                        :   t("consent_gate.title", "Documents requis")}
                    </Typography>

                    {/* Description */}
                    <Typography variant="body" align="center" sx={{ color: theme.custom.grey[2000] }}>
                        {hasRequiredDocuments ?
                            t("consent_gate.description", consentGateRequiredDocumentsMessage)
                        :   t(
                                "consent_gate.optional_preferences_info",
                                "These preferences are optional. You can change them at any time from your settings.",
                            )
                        }
                    </Typography>

                    {/* Info banner */}
                    {hasRequiredDocuments && (
                        <Box sx={{ width: "100%" }}>
                            <Stack
                                direction="row"
                                gap={theme.spacing(1.5)}
                                sx={{
                                    bgcolor: "rgba(255, 247, 235, 0.9)",
                                    borderLeft: `3px solid ${theme.palette.warning.dark}`,
                                    borderRadius: theme.spacing(1),
                                    color: theme.palette.warning.dark,
                                    p: theme.spacing(1.75, 2),
                                }}
                            >
                                <InfoOutlinedIcon fontSize="small" sx={{ flexShrink: 0, mt: "2px" }} />
                                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
                                    {t(
                                        "consent_gate.info_banner",
                                        "Ces documents contractuels sont requis pour votre organisation. Cliquez sur chaque titre pour le consulter avant de l'accepter.",
                                    )}
                                </Typography>
                            </Stack>
                        </Box>
                    )}

                    {/* Error display */}
                    {displayError && (
                        <Typography variant="body-medium" align="center" color="error">
                            {displayError}
                        </Typography>
                    )}

                    {/* Section: Required documents */}
                    {hasRequiredDocuments && (
                        <Box sx={{ width: "100%" }}>
                            <Typography
                                component="h2"
                                variant="body"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.custom.grey[2800],
                                    width: "100%",
                                    pb: theme.spacing(0.75),
                                    mb: theme.spacing(1.5),
                                    borderBottom: `1px solid ${theme.custom.grey[500]}`,
                                }}
                            >
                                {t("consent_gate.mandatory_documents", "Documents contractuels obligatoires")}
                            </Typography>

                            <Controller
                                name="versionIds"
                                control={control}
                                render={() => (
                                    <Stack gap={theme.spacing(1)}>
                                        {pendingConsentItems.map((item) => (
                                            <DocumentRow
                                                key={item.versionId}
                                                item={item}
                                                isChecked={selectedVersionIds.includes(item.versionId)}
                                                onToggle={() => toggleVersionId(item.versionId)}
                                                onViewDocument={() => openDocumentViewer(item)}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            />
                        </Box>
                    )}

                    {/* Section: Optional communication preferences */}
                    {unansweredConsentPreferences.length > 0 && (
                        <Box sx={{ width: "100%" }}>
                            {!isOptionalOnly && (
                                <Typography
                                    component="h2"
                                    variant="body"
                                    sx={{
                                        borderBottom: `1px solid ${theme.custom.grey[500]}`,
                                        color: theme.custom.grey[2800],
                                        fontWeight: 700,
                                        mb: theme.spacing(1.5),
                                        pb: theme.spacing(0.75),
                                        width: "100%",
                                    }}
                                >
                                    {t("consent_gate.optional_preferences", "Communication preferences (optional)")}
                                </Typography>
                            )}

                            <Stack gap={theme.spacing(1.5)}>
                                {!isOptionalOnly && (
                                    <Stack
                                        direction="row"
                                        gap={theme.spacing(1.5)}
                                        sx={{
                                            border: `1px solid ${theme.custom.grey[500]}`,
                                            borderRadius: theme.spacing(1),
                                            color: theme.custom.grey[2000],
                                            p: theme.spacing(1.75, 2),
                                        }}
                                    >
                                        <InfoOutlinedIcon fontSize="small" sx={{ flexShrink: 0, mt: "2px" }} />
                                        <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
                                            {t(
                                                "consent_gate.optional_preferences_info",
                                                "These preferences are optional. You can change them at any time from your settings.",
                                            )}
                                        </Typography>
                                    </Stack>
                                )}
                                <Stack gap={theme.spacing(1)}>
                                    {unansweredConsentPreferences.map((preference) => (
                                        <OptionalConsentRow
                                            key={preference.itemId}
                                            preference={preference}
                                            isChecked={selectedOptionalItemIds.has(preference.itemId)}
                                            onToggle={() => toggleOptionalPreference(preference.itemId)}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        </Box>
                    )}

                    {/* Footer */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            width: "100%",
                            pt: theme.spacing(2),
                            borderTop: `1px solid ${theme.custom.grey[500]}`,
                        }}
                    >
                        {hasRequiredDocuments && (
                            <Typography
                                variant="body2"
                                sx={{
                                    flex: 1,
                                    pr: theme.spacing(1.5),
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    color: allChecked ? theme.palette.success.main : theme.custom.grey[2000],
                                    fontSize: "11px",
                                }}
                            >
                                {allChecked ?
                                    <>
                                        <FoodsIcon icon="check" size={1.5} />
                                        {t("consent_gate.all_accepted", "All documents are accepted")}
                                    </>
                                :   t("consent_gate.counter", "{{selected}} / {{total}} document(s) accepted", {
                                        selected: selectedCount,
                                        total: totalCount,
                                    })
                                }
                            </Typography>
                        )}
                        <Button
                            variant="primary"
                            type="submit"
                            startIcon={<FoodsIcon icon="check" size={2} />}
                            disabled={!canValidate || isBusy}
                            sx={{ flexShrink: 0, ml: hasRequiredDocuments ? undefined : "auto" }}
                        >
                            {t("terms.login.button_validate")}
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            {/* Preferences note */}
            <Box
                sx={{
                    mt: theme.spacing(3),
                    p: theme.spacing(1.5, 2),
                    borderRadius: theme.spacing(1),
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${theme.custom.grey[500]}`,
                    width: "100%",
                    maxWidth: "520px",
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: theme.custom.grey[2000],
                        lineHeight: 1.6,
                        textAlign: "center",
                        fontSize: "12px",
                    }}
                >
                    {t(
                        "consent_gate.preferences_redirect_note",
                        "You can change these preferences at any time from Settings > Preferences.",
                    )}
                </Typography>
            </Box>

            {/* Document viewing modal */}
            <Dialog
                open={isDocumentDialogOpen}
                onClose={() => {
                    setIsDocumentDialogOpen(false);
                    setViewingDocument(null);
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: theme.spacing(2) },
                }}
            >
                {viewingDocument && (
                    <>
                        <DialogTitle sx={{ px: theme.spacing(4), pt: theme.spacing(3), pb: theme.spacing(2) }}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                gap={theme.spacing(2)}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 700, ...breakLongTextSx }}>
                                        {viewingDocument.name}
                                    </Typography>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        flexWrap="wrap"
                                        gap={1}
                                        sx={{ mt: 0.75 }}
                                    >
                                        {viewingDocument.version && (
                                            <FoodsTags label={`v${viewingDocument.version}`} variant="tags-grey" />
                                        )}
                                        {showViewingDocumentCategory && (
                                            <FoodsTags label={viewingDocumentCategoryLabel} variant="tags-grey" />
                                        )}
                                        <Typography
                                            variant="body2"
                                            sx={{ color: theme.custom.grey[2000], ...breakLongTextSx }}
                                        >
                                            {viewingDocument.name}
                                        </Typography>
                                    </Stack>
                                </Box>
                                <IconButton
                                    onClick={() => {
                                        setIsDocumentDialogOpen(false);
                                        setViewingDocument(null);
                                    }}
                                    size="small"
                                    sx={{ flexShrink: 0 }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </DialogTitle>
                        <Divider />
                        <DialogContent sx={{ maxHeight: "60vh", px: theme.spacing(4), py: theme.spacing(3) }}>
                            <Stack gap={theme.spacing(2)}>
                                {viewingDocument.summary && <HTMLBox value={viewingDocument.summary} />}
                                {viewingDocument.content && <HTMLBox value={viewingDocument.content} />}
                                {viewingDocument.documentUrl && (
                                    <Stack alignItems="flex-end">
                                        <Button
                                            component="a"
                                            href={viewingDocument.documentUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            variant="text"
                                            endIcon={<FoodsIcon icon="openNewTab" size={1} />}
                                            sx={{
                                                textDecoration: "none",
                                                "&:hover": {
                                                    textDecoration: "underline",
                                                },
                                            }}
                                        >
                                            {t("consent.drawer.open_document", "Ouvrir le document")}
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>
                        </DialogContent>
                        <Divider />
                        <DialogActions sx={{ px: theme.spacing(4), py: theme.spacing(2), gap: theme.spacing(1.5) }}>
                            <Button
                                type="button"
                                variant="secondary"
                                size="small"
                                onClick={() => {
                                    setIsDocumentDialogOpen(false);
                                    setViewingDocument(null);
                                }}
                            >
                                {t("common.close", "Close")}
                            </Button>
                            <Button type="button" variant="primary" size="small" onClick={acceptAndCloseDocument}>
                                {t("consent_gate.accept_and_check", "I have read and accept")}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </FormProvider>
    );
};
